/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { formatToRgb } from "../utils/helpers/formatToRgb";
import { WIDGET_CONTAINER_NAME } from "../constants/constants";
import { DEFAULT_THEME } from "../constants/constants";

const defaultTheme = {
  containerName: WIDGET_CONTAINER_NAME,
  breakpoints: {
    xs: "320px",
    sm: "576px",
    md: "768px",
    lg: "1024px",
    xxl: "1400px",
  },
  media: {
    sm: `@media (max-width: 576px)`,
    md: `@media (max-width: 768px)`,
    lg: `@media (max-width: 1024px)`,
    xxl: `@media (max-width: 1400px)`,
  },
  colors: DEFAULT_THEME.colors,
  fontSizes: DEFAULT_THEME.fontSizes,
  borderRadius: DEFAULT_THEME.borderRadius,
  containerMedia: {
    sm: `@container ${WIDGET_CONTAINER_NAME} (max-width: 576px)`,
    md: `@container ${WIDGET_CONTAINER_NAME} (max-width: 768px)`,
    lg: `@container ${WIDGET_CONTAINER_NAME} (max-width: 1024px)`,
    xxl: `@container ${WIDGET_CONTAINER_NAME} (max-width: 1400px)`,
  },
};

const getCssVariable = (variable) => {
  if (typeof window !== "undefined") {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  }
  return "";
};

const isPlainValue = (value) => /^\d+,\s*\d+,\s*\d+(,\s*[0-1](\.\d+)?)?$/.test(value);

const isOpaque = (rgba) => {
  const parts = rgba.split(",");
  return parts.length === 4 && parseFloat(parts[3]) === 1;
};

const processColorValue = (value) => {
  const { rgb, rgba } = formatToRgb(value);

  return {
    rgb: rgb,
    rgba: rgba,
  };
};

const resolveCssVarColor = (key) => {
  const cssVar = getCssVariable(`--sbm-${key}`);
  const cssVarRgb = getCssVariable(`--sbm-${key}-rgb`);

  if (isPlainValue(cssVar) && isPlainValue(cssVarRgb)) {
    return {
      rgb: `var(--sbm-${key}-rgb)`,
      rgba: `var(--sbm-${key})`,
    };
  }

  if (cssVar || cssVarRgb) {
    return processColorValue(cssVar || cssVarRgb);
  }

  return null;
};

const useWidgetTheme = (theme) => {
  const formattedThemeColors = useMemo(() => {
    if (!theme) return defaultTheme.colors;

    return {
      accent: theme?.colors_accent || defaultTheme.colors.accent,
      accentContrast: theme?.colors_accent_contrast || defaultTheme.colors.accentContrast,
      text: theme?.colors_text || defaultTheme.colors.text,
      textSecondary: theme?.colors_text_secondary || defaultTheme.colors.textSecondary,
      border: theme?.colors_border || defaultTheme.colors.border,
      background: theme?.colors_background || defaultTheme.colors.background,
      link: theme?.colors_link || defaultTheme.colors.link,
      success: defaultTheme.colors.success,
      error: defaultTheme.colors.error,
      warning: defaultTheme.colors.warning,
      iconStroke: defaultTheme.colors.iconStroke,
    };
  }, [theme]);

  const themeSettings = useMemo(() => {
    const processedColors = Object.fromEntries(
      Object.entries(formattedThemeColors).map(([key, value]) => [
        key,
        resolveCssVarColor(key) || processColorValue(value),
      ])
    );

    // Floating layers (modal, popover, ...): host's surface, else background, else white when background is transparent
    const rawBackground =
      getCssVariable("--sbm-background") || getCssVariable("--sbm-background-rgb") || formattedThemeColors.background;
    processedColors.surface =
      resolveCssVarColor("surface") ||
      (theme?.colors_surface && processColorValue(theme.colors_surface)) ||
      (isOpaque(processColorValue(rawBackground).rgba)
        ? processedColors.background
        : processColorValue(DEFAULT_THEME.colors.background));

    const processedFontSizes = {
      h2: theme?.font_sizes_h2 || defaultTheme.fontSizes.h2,
      h3: theme?.font_sizes_h3 || defaultTheme.fontSizes.h3,
      p: theme?.font_sizes_p || defaultTheme.fontSizes.p,
    };

    return {
      ...defaultTheme,
      colors: processedColors,
      fontSizes: processedFontSizes,
    };
  }, [formattedThemeColors, theme]);

  return themeSettings;
};

export default useWidgetTheme;
